import { Mark, mergeAttributes } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    comment: {
      setComment: (commentId: string) => ReturnType;
      unsetComment: () => ReturnType;
      removeCommentById: (commentId: string) => ReturnType;
    };
  }
}

export const CommentMark = Mark.create({
  name: 'comment',

  // Prevent overlapping comments
  excludes: 'comment',

  // Prevent comment mark from extending across line breaks
  inclusive: false,

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: element => element.getAttribute('data-comment-id'),
        renderHTML: attributes => {
          if (!attributes.commentId) return {};
          return { 'data-comment-id': attributes.commentId };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-comment-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'tiptap-comment-highlight',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setComment:
        (commentId: string) =>
        ({ commands }) => {
          return commands.setMark(this.name, { commentId });
        },
      unsetComment:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
      removeCommentById:
        (commentId: string) =>
        ({ tr, state, dispatch }) => {
          // Find and remove all marks with the given commentId
          const { doc } = state;
          const marksToRemove: Array<{ from: number; to: number }> = [];

          doc.descendants((node, pos) => {
            if (!node.isText) return;

            node.marks.forEach(mark => {
              if (
                mark.type.name === 'comment' &&
                mark.attrs.commentId === commentId
              ) {
                marksToRemove.push({
                  from: pos,
                  to: pos + node.nodeSize,
                });
              }
            });
          });

          marksToRemove.reverse().forEach(({ from, to }) => {
            tr.removeMark(from, to, this.type);
          });

          const modified = marksToRemove.length > 0;

          if (dispatch && modified) {
            dispatch(tr);
          }

          return modified;
        },
    };
  },
});
